// Dependencies
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useRef } from 'react';

// Types
export interface FilterParams {
  page?: number;
  perPage?: number;
  sort?: string;
  search?: string;
  archived?: boolean;
  backups?: boolean;
  [key: string]: any;
}

// Constants
const STORAGE_PREFIX = 'page_filters_';

// Utility functions for URL parameters and localStorage
export const useUrlParams = (pageKey: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get stored filters from localStorage
  const getStoredFilters = (): FilterParams => {
    try {
      const stored = localStorage.getItem(`${STORAGE_PREFIX}${pageKey}`);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error parsing stored filters:', error);
      return {};
    }
  };

  // Save filters to localStorage
  const saveFiltersToStorage = (filters: FilterParams) => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${pageKey}`, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters to storage:', error);
    }
  };

  // Get current URL parameters
  const getUrlParams = (): FilterParams => {
    const params: FilterParams = {};
    
    // Parse URL parameters
    const page = searchParams.get('page');
    const perPage = searchParams.get('perPage');
    const sort = searchParams.get('sort');
    const search = searchParams.get('search');
    const archived = searchParams.get('archived');
    const backups = searchParams.get('backups');
    const customer = searchParams.get('customer');

    if (page) params.page = parseInt(page, 10);
    if (perPage) params.perPage = parseInt(perPage, 10);
    if (sort) params.sort = sort;
    if (search) params.search = search;
    if (archived) params.archived = archived === 'true';
    if (backups) params.backups = backups === 'true';
    if (customer) params.customer = customer;

    return params;
  };

  // Debounced URL update for search parameter
  const debouncedUpdateRef = useRef<NodeJS.Timeout | null>(null);

  // Update URL parameters (search updates are handled separately after API response)
  const updateUrlParams = useCallback((newParams: Partial<FilterParams>) => {
    const currentParams = getUrlParams();
    const updatedParams = { ...currentParams, ...newParams };

    // Remove undefined/null values
    Object.keys(updatedParams).forEach(key => {
      if (updatedParams[key] === undefined || updatedParams[key] === null) {
        delete updatedParams[key];
      }
    });

    // Update URL
    const newSearchParams = new URLSearchParams();
    Object.entries(updatedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        newSearchParams.set(key, String(value));
      }
    });

    setSearchParams(newSearchParams);
    
    // Save to localStorage
    saveFiltersToStorage(updatedParams);
  }, [searchParams, setSearchParams]);

  // Update URL parameters after successful API response (for search)
  const updateUrlParamsAfterApiResponse = useCallback((newParams: Partial<FilterParams>) => {
    const currentParams = getUrlParams();
    const updatedParams = { ...currentParams, ...newParams };

    // Remove undefined/null values
    Object.keys(updatedParams).forEach(key => {
      if (updatedParams[key] === undefined || updatedParams[key] === null) {
        delete updatedParams[key];
      }
    });

    // Update URL
    const newSearchParams = new URLSearchParams();
    Object.entries(updatedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        newSearchParams.set(key, String(value));
      }
    });

    setSearchParams(newSearchParams);
    
    // Save to localStorage
    saveFiltersToStorage(updatedParams);
  }, [searchParams, setSearchParams]);

  // Initialize filters from URL or localStorage
  const initializeFilters = (defaultFilters: FilterParams = {}): FilterParams => {
    const urlParams = getUrlParams();
    const storedParams = getStoredFilters();
    
    // If URL has parameters, use them and update localStorage
    if (Object.keys(urlParams).length > 0) {
      saveFiltersToStorage(urlParams);
      return { ...defaultFilters, ...urlParams };
    }
    
    // If no URL parameters but stored filters exist, use them and update URL
    if (Object.keys(storedParams).length > 0) {
      // Update URL with stored params
      const newSearchParams = new URLSearchParams();
      Object.entries(storedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          newSearchParams.set(key, String(value));
        }
      });
      setSearchParams(newSearchParams);
      
      return { ...defaultFilters, ...storedParams };
    }
    
    // Use default filters
    return defaultFilters;
  };

  // Restore filters from localStorage (for when returning from other pages)
  const restoreFromStorage = (): FilterParams => {
    const storedParams = getStoredFilters();
    
    if (Object.keys(storedParams).length > 0) {
      // Update URL with stored params
      const newSearchParams = new URLSearchParams();
      Object.entries(storedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          newSearchParams.set(key, String(value));
        }
      });
      setSearchParams(newSearchParams);
      
      return storedParams;
    }
    return {};
  };

  // Clear filters
  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
    localStorage.removeItem(`${STORAGE_PREFIX}${pageKey}`);
  };

  return {
    getUrlParams,
    updateUrlParams,
    updateUrlParamsAfterApiResponse,
    initializeFilters,
    restoreFromStorage,
    clearFilters,
    searchParams,
    location,
  };
}; 